// ============================================================
//  EXPERTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/ExpertsPage.tsx
//
//  Access: super_admin, admin, customer_service (view-only)
//
//  Lists Expert records (NOT User records — an expert has both
//  a User row and a separate Expert row; this page queries the
//  Expert table via GET /api/admin/experts). The "View" link
//  below deliberately uses `ex.id` (the Expert's own primary
//  key), not `ex.user.id` / `ex.userId` — ExpertDetailPage looks
//  up experts by their Expert id, so linking with the wrong id
//  is what causes "Expert not found" on the detail page.
//
//  FIX: the status filter now follows the URL (?status=pending).
//  DashboardPage's "Review Experts — N pending" quick action links to
//  /experts?status=pending, but this page ignored the query string and
//  always opened on "All Statuses". Changing the dropdown also updates
//  the URL, so a filtered list can be bookmarked or refreshed.
//
//  CHANGE (2026-10-10): new "Support" column — help requests from this
//  tasker's app that nobody has answered yet (red number, links to Support
//  filtered to that person). Loaded once per page of results.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { listExperts } from '../services/admin.service'
import { Expert } from '../types'
import { getSupportOpenSummaryApi, SupportOpenSummary } from '../api/support.api'

const STATUS_OPTIONS = [
  { value: '',                 label: 'All Statuses' },
  { value: 'pending',          label: 'Pending' },
  { value: 'under_review',     label: 'Under Review' },
  { value: 'background_check', label: 'Background Check' },
  { value: 'approved',         label: 'Approved' },
  { value: 'rejected',         label: 'Rejected' },
  { value: 'suspended',        label: 'Suspended' },
]

const STATUS_COLORS: Record<string, string> = {
  pending:           'bg-yellow-600/20 text-yellow-400',
  under_review:      'bg-blue-600/20 text-blue-400',
  background_check:  'bg-purple-600/20 text-purple-400',
  approved:          'bg-green-600/20 text-green-400',
  rejected:          'bg-red-600/20 text-red-400',
  suspended:         'bg-gray-600/20 text-gray-400',
}

const PAGE_SIZE = 20

const ExpertsPage: React.FC = () => {
  const [experts, setExperts]           = useState<Expert[]>([])
  // Open help requests per user id, for the rows on this page.
  const [supportByUser, setSupportByUser] = useState<SupportOpenSummary['byUser']>({})
  const [total, setTotal]               = useState(0)
  const [page, setPage]                 = useState(1)
  const [searchParams, setSearchParams] = useSearchParams()
  // Only values the dropdown actually offers are accepted from the URL —
  // anything else falls back to "All Statuses".
  const urlStatus = searchParams.get('status') || ''
  const statusFilter = STATUS_OPTIONS.some((o) => o.value === urlStatus) ? urlStatus : ''
  const setStatusFilter = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('status', value)
    else next.delete('status')
    setSearchParams(next, { replace: true })
  }
  // Same URL-following pattern as status above — a link like
  // /experts?issuesOnly=true can be bookmarked or shared.
  const issuesOnly = searchParams.get('issuesOnly') === 'true'
  const setIssuesOnly = (value: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('issuesOnly', 'true')
    else next.delete('issuesOnly')
    setSearchParams(next, { replace: true })
  }
  // Same URL-following pattern — a decision is outstanding (pending
  // approval, awaiting certification) but nothing is wrong, so this is
  // deliberately a separate filter from "Has Issues" above.
  const needsActionOnly = searchParams.get('needsActionOnly') === 'true'
  const setNeedsActionOnly = (value: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('needsActionOnly', 'true')
    else next.delete('needsActionOnly')
    setSearchParams(next, { replace: true })
  }
  // Same URL-following pattern: taskers who have not answered the GST/HST question.
  const taxNotDeclared = searchParams.get('taxNotDeclared') === 'true'
  const setTaxNotDeclared = (value: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('taxNotDeclared', 'true')
    else next.delete('taxNotDeclared')
    setSearchParams(next, { replace: true })
  }
  // Same URL-following pattern: approved taskers still finishing payout / GST-HST.
  const setupPending = searchParams.get('setupPending') === 'true'
  const setSetupPending = (value: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('setupPending', 'true')
    else next.delete('setupPending')
    setSearchParams(next, { replace: true })
  }
  const [loading, setLoading]           = useState(true)
  const [error, setError]               = useState('')

  const fetchExperts = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await listExperts({
        page, limit: PAGE_SIZE,
        status: statusFilter || undefined,
        ...(issuesOnly ? { issuesOnly: 'true' } : {}),
        ...(needsActionOnly ? { needsActionOnly: 'true' } : {}),
        ...(taxNotDeclared ? { taxNotDeclared: 'true' } : {}),
        ...(setupPending ? { setupPending: 'true' } : {}),
      })
      setExperts(res.data.data)
      setTotal(res.data.pagination.total)
      // Support column: one request for the whole page. A failure only
      // leaves the column empty.
      const userIds = (res.data.data || []).map((e: any) => e.userId || e.user?.id).filter(Boolean)
      if (userIds.length) {
        getSupportOpenSummaryApi(userIds)
          .then((r) => setSupportByUser(r.data?.data?.byUser || {}))
          .catch(() => setSupportByUser({}))
      } else {
        setSupportByUser({})
      }
    } catch (err: any) {
      console.error('Failed to fetch experts:', err)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load experts'))
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, issuesOnly, needsActionOnly, taxNotDeclared, setupPending])

  useEffect(() => { fetchExperts() }, [fetchExperts])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Experts</h1>
          <p className="text-gray-400 text-sm mt-1">{total} total experts</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          onClick={() => { setIssuesOnly(!issuesOnly); setPage(1) }}
          title="Suspended/rejected, failed background check, rejected documents, subscription past due, failed payouts, or open disputes"
          className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
            issuesOnly ? 'bg-red-600 text-white' : 'bg-surface-tertiary border border-border-light text-gray-400 hover:text-white'
          }`}>
          ⚠️ Has Issues
        </button>
        <button
          onClick={() => { setNeedsActionOnly(!needsActionOnly); setPage(1) }}
          title="Awaiting an admin decision — pending approval, or documents submitted awaiting certification. Nothing is wrong; a decision just hasn't been made yet."
          className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
            needsActionOnly ? 'bg-brand-600 text-white' : 'bg-surface-tertiary border border-border-light text-gray-400 hover:text-white'
          }`}>
          🔔 Needs Action
        </button>
        <button
          onClick={() => { setTaxNotDeclared(!taxNotDeclared); setPage(1) }}
          title="Taskers who have not yet said whether they are registered for GST/HST. Until they do, no tax is charged on their job price."
          className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
            taxNotDeclared ? 'bg-amber-600 text-white' : 'bg-surface-tertiary border border-border-light text-gray-400 hover:text-white'
          }`}>
          🧾 GST/HST not declared
        </button>
        <button
          onClick={() => { setSetupPending(!setupPending); setPage(1) }}
          title="Approved taskers who still have to finish payout (verified by Stripe) and/or declare GST/HST. The app stays on the setup screen for them."
          className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
            setupPending ? 'bg-yellow-600 text-white' : 'bg-surface-tertiary border border-border-light text-gray-400 hover:text-white'
          }`}>
          ⏳ Finishing setup
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => fetchExperts()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Contact</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Country</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Province</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">City</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Type</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Issues</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Needs Action</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Support</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">GST/HST</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Payout</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Online</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Last Seen</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Rate</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Submitted</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={17} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : error ? (
              // Don't show "No experts found" under a load error — that
              // reads as real data. The banner above has the retry.
              <tr><td colSpan={17} className="text-center py-8 text-gray-500">—</td></tr>
            ) : experts.length === 0 ? (
              <tr><td colSpan={17} className="text-center py-8 text-gray-400">No experts found</td></tr>
            ) : (
              experts.map((ex) => {
                const isCompany = ex.expertType === 'company'
                return (
                  <tr key={ex.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-700 flex items-center justify-center text-white text-sm font-bold">
                          {ex.user?.firstName?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {ex.user ? `${ex.user.firstName} ${ex.user.lastName}` : 'Unknown user'}
                          </p>
                          {/* Expert id, not user id — this is what the detail route expects */}
                          <p className="text-xs text-gray-500">{ex.id.slice(0, 12)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-gray-300">{ex.user?.email || '—'}</p>
                      <p className="text-xs text-gray-500">{ex.user?.phone || '—'}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-300">{(ex as any).addressCountry || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">{(ex as any).addressProvince || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">{(ex as any).addressCity || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">
                      {isCompany ? 'Company' : 'Individual'}
                      {/* Option A: chose the company track at signup but
                          hasn't been approved yet, so expertType is still
                          'individual' — see admin.service.js's
                          applyPendingCompanyConversion, which is what
                          flips this the moment they're approved. Without
                          this, an admin reviewing this applicant has no
                          way to know a company registration is coming. */}
                      {!isCompany && ex.pendingCompanyConversion && (
                        <span className="block text-xs text-brand-400 mt-0.5" title={ex.businessName ? `Registering as: ${ex.businessName}` : undefined}>
                          → Company (pending approval)
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[ex.status] || 'bg-gray-600/20 text-gray-400'}`}>
                        {ex.status.replace('_', ' ')}
                      </span>
                      {/* Approved, but payout (Stripe) and/or GST/HST still open: on the "Finish setup" screen. */}
                      {ex.setupPending && (
                        <span className={`block text-xs mt-1 ${(ex.daysSinceApproval ?? 0) >= 3 ? 'text-red-400' : 'text-yellow-400'}`}
                          title="Approved, but still has to finish payout (verified by Stripe) and/or declare GST/HST before the app opens for them.">
                          Finishing setup{ex.daysSinceApproval != null ? ` · day ${ex.daysSinceApproval}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {(ex as any).issues && (ex as any).issues.length > 0 ? (
                        <span
                          title={(ex as any).issues.map((i: any) => i.message).join('\n')}
                          className={`px-2 py-1 text-xs rounded-full ${
                            (ex as any).issues.some((i: any) => i.severity === 'blocking') ? 'bg-red-600/20 text-red-400' : 'bg-yellow-600/20 text-yellow-400'
                          }`}>
                          ⚠️ {(ex as any).issues.length}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {ex.needsAction && ex.needsAction.length > 0 ? (
                        <span
                          title={ex.needsAction.map((a) => a.message).join('\n')}
                          className="px-2 py-1 text-xs rounded-full bg-brand-600/20 text-brand-400">
                          🔔 {ex.needsAction.length}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {(() => {
                        const uid = (ex as any).userId || (ex as any).user?.id
                        const n = uid ? (supportByUser[uid]?.expert || 0) : 0
                        return n > 0 ? (
                          <Link
                            to={`/support?search=${encodeURIComponent(uid)}&appType=expert&status=open`}
                            title="Help requests from this tasker that nobody has answered yet"
                            className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30">
                            💬 {n}
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )
                      })()}
                    </td>
                    <td className="px-5 py-4">
                      {/* GST/HST answer: never answered / registered (and how they price) / not registered */}
                      {!(ex as any).taxDeclaredAt ? (
                        <span title="This tasker has not answered the GST/HST question yet. No tax is charged on their job price until they do."
                          className="px-2 py-1 text-xs rounded-full bg-amber-600/20 text-amber-400 whitespace-nowrap">Not declared</span>
                      ) : (ex as any).taxRegistered ? (
                        <span title={(ex as any).gstHstNumber ? `GST/HST number ${(ex as any).gstHstNumber}` : undefined}
                          className="px-2 py-1 text-xs rounded-full bg-emerald-600/20 text-emerald-400 whitespace-nowrap">
                          Registered · {(ex as any).taxPricingMode === 'TAX_INCLUDED' ? 'tax included' : 'tax not included'}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-600/20 text-gray-400 whitespace-nowrap">Not registered</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {/* Latest Stripe payout status. Red when the tasker is approved but cannot take paid jobs. */}
                      {ex.payout ? (
                        !ex.payout.required ? (
                          <span title="All of this tasker's categories are paid outside the app." className="text-xs text-gray-600">Not needed</span>
                        ) : ex.payout.flag ? (
                          <span title="Approved, but payout not active — cannot be hired for paid jobs until fixed in Stripe."
                            className="px-2 py-1 text-xs rounded-full bg-red-600/20 text-red-400 whitespace-nowrap">⚠ {ex.payout.label}</span>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${ex.payout.active ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>{ex.payout.label}</span>
                        )
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {/* isOnline: tasker's manual availability toggle */}
                      {ex.isOnline
                        ? <span className="px-2 py-1 text-xs rounded-full bg-green-600/20 text-green-400">🟢 Online</span>
                        : <span className="px-2 py-1 text-xs rounded-full bg-surface-tertiary text-gray-500">⚫ Offline</span>
                      }
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400">
                      {/* lastSeenAt: updated on every app API call — more honest
                          than isOnline alone (shows activity even when offline) */}
                      {ex.lastSeenAt
                        ? (() => {
                            const diff = Date.now() - new Date(ex.lastSeenAt as string).getTime()
                            const mins  = Math.floor(diff / 60000)
                            const hours = Math.floor(diff / 3600000)
                            const days  = Math.floor(diff / 86400000)
                            if (mins < 5)   return <span className="text-green-400">Just now</span>
                            if (hours < 1)  return <span className="text-green-400">{mins}m ago</span>
                            if (hours < 24) return <span className="text-yellow-400">{hours}h ago</span>
                            if (days < 7)   return <span className="text-orange-400">{days}d ago</span>
                            return <span className="text-gray-500">{days}d ago</span>
                          })()
                        : <span className="text-gray-600">—</span>
                      }
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-300">
                      {ex.rate != null ? `$${ex.rate}/hr` : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400">
                      {ex.submittedAt ? new Date(ex.submittedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <Link
                        to={`/experts/${ex.id}`}
                        className="px-3 py-1.5 text-xs bg-brand-500/10 text-brand-400 rounded-lg hover:bg-brand-500/20 transition-colors inline-block"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
            <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExpertsPage